// ⌘K：一处搜全部。知识库按名字在本机筛；资料按标题、文件名；段落用和问答同一套检索；以前的对话也搜。
// 上下键移动，Enter 打开，Esc 关闭。

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "antd";
import { FileText, Library, MessageSquare, Quote, Search } from "lucide-react";
import {
  searchByName,
  searchKnowledge,
  searchMessages,
} from "../../api/search";
import { clipSnippet } from "../../lib/citations";
import { useKnowledgeBases } from "../chat/Composer";
import { useDocPreview } from "../kb/usePreview";
import { cleanTitle, docTitle } from "../kb/DocCard";
import "./search.css";
import { listTasks } from "../../api/hub/work";

interface Item {
  key: string;
  group: string;
  icon: React.ReactNode;
  title: string;
  detail?: string;
  run: () => void;
}

function useDebounced(value: string, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function SearchPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const q = useDebounced(text.trim());
  const navigate = useNavigate();
  const location = useLocation();
  const preview = useDocPreview();
  const kbs = useKnowledgeBases().data ?? [];
  const listRef = useRef<HTMLDivElement>(null);
  const enabled = open && q.length >= 1;
  const tasks = useQuery({ queryKey: ["hub", "tasks"], queryFn: () => listTasks(), enabled: open });
  const names = useQuery({
    queryKey: ["search-names", q],
    enabled,
    queryFn: () => searchByName(q),
  });
  const passages = useQuery({
    queryKey: ["search-passages", q],
    enabled: enabled && q.length >= 2 && kbs.length > 0,
    queryFn: () =>
      searchKnowledge(
        q,
        kbs.map((k) => k.id),
      ),
  });
  const chats = useQuery({
    queryKey: ["search-chats", q],
    enabled: enabled && q.length >= 2,
    queryFn: () => searchMessages(q),
  });

  useEffect(() => {
    if (!open) setText("");
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const done = (fn: () => void) => () => {
      onClose();
      fn();
    };
    const out: Item[] = [];
    const command = (key: string, title: string, path: string, detail?: string) => {
      if (!q || `${title} ${detail ?? ""}`.toLowerCase().includes(q.toLowerCase()))
        out.push({ key, title, group: "命令", icon: <Search />, detail, run: done(() => navigate(path)) });
    };
    const selectedTask = new URLSearchParams(location.search).get("task");
    if (location.pathname === "/tasks" && selectedTask) {
      for (const [field, title, shortcut] of [
        ["assigneeId", "指派任务", "A"], ["priority", "修改优先级", "P"],
        ["status", "修改状态", "S"], ["dueDate", "修改截止日", "D"],
        ["domain", "修改业务域", ""], ["projectId", "修改所属项目", ""],
      ]) command(`task-field:${field}`, title, `/tasks?task=${encodeURIComponent(selectedTask)}&field=${field}`, shortcut ? `快捷键 ${shortcut} · 保存后生效` : "保存后生效");
    }
    command("task-create", "新建任务", "/tasks?create=1", "快捷键 C");
    if (q) out.push(
      {
        key: "ask",
        group: "命令",
        icon: <MessageSquare />,
        title: `提问：${q}`,
        detail: "带到问答输入框，确认后发送",
        run: done(() => navigate("/ask", { state: { topic: q } })),
      },
    );
    for (const [path, label] of [
      ["/", "焦点"],
      ["/tasks", "任务"],
      ["/projects", "项目"],
      ["/calendar", "月历"],
      ["/inbox", "收件箱"],
      ["/settings/platform", "中台设置"],
      ["/approvals", "审批"], ["/okr", "OKR"], ["/inspiration", "灵感墙"],
      ["/customers", "客户"], ["/ask", "问答"], ["/kb", "资料库"],
    ])
      if (!q || label.toLowerCase().includes(q.toLowerCase()))
        out.push({
          key: path,
          group: "命令",
          icon: <Search />,
          title: `打开${label}`,
          run: done(() => navigate(path)),
        });
    if (!q) return out;
    for (const task of (tasks.data ?? []).filter(t => t.status !== "triage" && `${t.identifier} ${t.title}`.toLowerCase().includes(q.toLowerCase())).slice(0, 6))
      out.push({ key: `task:${task.id}`, group: "任务", icon: <FileText />, title: `${task.identifier} ${task.title}`, run: done(() => navigate(`/tasks?task=${encodeURIComponent(task.id)}`)) });
    for (const kb of kbs
      .filter((k) => k.name.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 3))
      out.push({
        key: `kb:${kb.id}`,
        group: "知识库",
        icon: <Library />,
        title: kb.name,
        detail: `${kb.knowledge_count ?? 0} 篇`,
        run: done(() => navigate(`/kb/${kb.id}`)),
      });
    for (const d of (names.data ?? []).slice(0, 6))
      out.push({
        key: `doc:${d.id}`,
        group: "资料",
        icon: <FileText />,
        title: docTitle(d),
        detail: d.knowledge_base_name,
        run: done(() => preview.open(d.id, d.knowledge_base_id)),
      });
    const seen = new Set<string>();
    for (const h of passages.data ?? []) {
      if (seen.has(h.knowledge_id) || seen.size >= 5) continue;
      seen.add(h.knowledge_id);
      out.push({
        key: `p:${h.id ?? h.knowledge_id}`,
        group: "段落",
        icon: <Quote />,
        title:
          cleanTitle(h.knowledge_title || h.knowledge_filename || "") || "资料",
        detail: clipSnippet(h.content),
        run: done(() => preview.open(h.knowledge_id, h.knowledge_base_id)),
      });
    }
    for (const m of (chats.data ?? []).slice(0, 5))
      out.push({
        key: `m:${m.request_id}`,
        group: "对话",
        icon: <MessageSquare />,
        title: m.session_title || m.query_content,
        detail: clipSnippet(m.query_content),
        run: done(() => navigate(`/chat/${m.session_id}`)),
      });
    return out;
  }, [
    q,
    kbs,
    names.data,
    passages.data,
    chats.data,
    navigate,
    onClose,
    preview,
    location.pathname,
    location.search,
    tasks.data,
  ]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const loading =
    enabled && (names.isFetching || passages.isFetching || chats.isFetching);
  let lastGroup = "";
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={620}
      className="palette"
      destroyOnHidden
      centered={false}
      style={{ top: "12vh" }}
      aria-label="搜索"
    >
      <label className="palette-input">
        <Search width={18} height={18} />
        <input
          autoFocus
          value={text}
          placeholder="执行命令、找任务、搜索资料，或直接提问…"
          aria-label="搜索"
          aria-controls="palette-list"
          aria-activedescendant={
            items[active] ? `palette-${active}` : undefined
          }
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, items.length - 1));
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            }
            if (e.key === "Enter" && items[active]) {
              e.preventDefault();
              items[active].run();
            }
          }}
        />
        <kbd>Esc</kbd>
      </label>
      <div
        id="palette-list"
        ref={listRef}
        className="palette-list"
        role="listbox"
        aria-busy={loading}
      >
        {q && !loading && items.length === 0 && (
          <p className="palette-empty">没有找到「{q}」</p>
        )}
        {items.map((it, i) => {
          const header =
            it.group !== lastGroup ? (
              <div className="palette-group" role="presentation">
                {(lastGroup = it.group)}
              </div>
            ) : null;
          return (
            <div key={it.key}>
              {header}
              <button
                id={`palette-${i}`}
                type="button"
                role="option"
                aria-selected={i === active}
                data-index={i}
                className="palette-item"
                onMouseEnter={() => setActive(i)}
                onClick={it.run}
              >
                {it.icon}
                <span className="palette-text">
                  <span className="palette-title">{it.title}</span>
                  {it.detail && (
                    <span className="palette-detail">{it.detail}</span>
                  )}
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
